import { Ref, computed, unref } from '@nuxtjs/composition-api';
import { Cart } from '@vsf-enterprise/sap-commerce-webservices-sdk';
import { BaseProps } from '@vsf-enterprise/sapcc-api';
import { useVSFContext, sharedRef, Logger } from '@vue-storefront/core';
import { CloneCartProps, LoadCartProps, SaveCartProps, UseCartComposable, UseCartError, UseCartLoading } from '../types';
import * as actions from './actions';

export const useCart = (id?: string): UseCartComposable => {
  const context = useVSFContext();
  const cart: Ref<Cart> = sharedRef(null, `useCart-cart-${id}`);
  const loading: Ref<UseCartLoading> = sharedRef({
    load: false,
    save: false,
    restore: false,
    clear: false,
    clone: false
  }, `useCart-loading-${id}`);
  const error: Ref<UseCartError> = sharedRef({
    load: null,
    save: null,
    restore: null,
    clear: null,
    clone: null
  }, `useCart-error-${id}`);

  const methodController = async (
    methodName: string,
    callback: () => Promise<void>
  ) => {
    Logger.debug(`useCart-${id}.${methodName}`);

    try {
      loading.value[methodName] = true;
      await callback();
      error.value[methodName] = null;
    } catch (err) {
      error.value[methodName] = err;
      Logger.error(`useCart-${id}.${methodName}`, err);
    } finally {
      loading.value[methodName] = false;
    }
  };

  const load = async (props?: LoadCartProps) => {
    await methodController('load', async () => {
      cart.value = await actions.loadCart(context, props || {}, id);
    });
  };

  const clear = async () => {
    await methodController('clear', async () => {
      await actions.clearCart(context, unref(cart), id);
      cart.value = null;
    });
  };

  const save = async (props?: SaveCartProps) => {
    await methodController('save', async () => {
      cart.value = await actions.saveCart(context, props || {}, unref(cart));
    });
  };

  const restore = async (props?: BaseProps) => {
    await methodController('restore', async () => {
      cart.value = await actions.restoreCart(context, props || {}, unref(cart));
    });
  };

  const clone = async (props?: CloneCartProps) => {
    let clonedCart: Cart;

    await methodController('clone', async () => {
      clonedCart = await actions.cloneCart(context, props || {}, unref(cart));
    });

    return clonedCart;
  };

  const set = (newCart: Cart) => {
    cart.value = actions.setCart(context, newCart, id);
  };

  return {
    cart: computed(() => cart.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    load,
    clear,
    save,
    restore,
    clone,
    set
  };
};
